/*
  TikTok Showcase (System C · Epic #60 / Issue #65)

  Two behaviours, one file — only one branch is ever present on a given page:

  1. FACADE click-to-load (interim fallback branch) — ported from the retired
     sections/social-proof-carousel.liquid (#56). The third-party player iframe
     is injected on TAP only, so there are ZERO requests to tiktok.com /
     youtube.com on initial page load. One delegated listener serves every card.

  2. INTERSECTIONOBSERVER autoplay (store-backed branch) — a SINGLE observer
     instance swaps data-src→src (once) and plays only the card in view, pausing
     off-screen cards. prefers-reduced-motion renders posters only (never
     autoplays). The <video> ships with preload="none" and no src, so no video
     bytes load until a card approaches the viewport (CWV budget).
*/
(function () {
  'use strict';

  /* ---- 1. Facade click-to-load (fallback branch) ---------------------- */
  function onFacadeClick(event) {
    var facade = event.target.closest('[data-tiktok-facade]');
    if (!facade) return;

    var embed = facade.closest('[data-tiktok-embed]');
    if (!embed || embed.dataset.hydrated === 'true') return;

    var platform = facade.dataset.platform;
    var src = '';
    if (platform === 'tiktok' && facade.dataset.tiktokId) {
      src = 'https://www.tiktok.com/embed/v2/' + facade.dataset.tiktokId;
    } else if (platform === 'youtube' && facade.dataset.youtubeId) {
      src = 'https://www.youtube.com/embed/' + facade.dataset.youtubeId + '?autoplay=1';
    } else {
      return;
    }

    embed.dataset.hydrated = 'true';

    var iframe = document.createElement('iframe');
    iframe.className = 'tiktok-showcase__iframe';
    iframe.src = src;
    iframe.title = facade.getAttribute('aria-label') || 'Creator video';
    iframe.setAttribute(
      'allow',
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
    );
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('scrolling', 'no');

    embed.innerHTML = '';
    embed.appendChild(iframe);
  }

  /* ---- 2. IntersectionObserver autoplay (store-backed branch) --------- */
  var videoObserver = null;

  function initVideoAutoplay() {
    var videos = document.querySelectorAll('[data-tiktok-video]');
    if (!videos.length) return;

    var reduceMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Posters only: leave the preload="none" videos untouched (no src, no play).
    if (reduceMotion || !('IntersectionObserver' in window)) return;

    if (!videoObserver) {
      videoObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var video = entry.target;
            if (entry.isIntersecting) {
              if (!video.getAttribute('src') && video.dataset.src) {
                video.setAttribute('src', video.dataset.src);
              }
              var playResult = video.play();
              if (playResult && typeof playResult.catch === 'function') {
                // Autoplay may be blocked (older Safari, low-power mode) — the
                // poster remains; not a failure state.
                playResult.catch(function () {});
              }
              video.dataset.playing = 'true';
            } else {
              video.pause();
              video.dataset.playing = 'false';
            }
          });
        },
        { threshold: 0.5 }
      );
    }

    videos.forEach(function (video) {
      if (video.dataset.observed === 'true') return;
      video.dataset.observed = 'true';
      videoObserver.observe(video);
    });
  }

  /* ---- Init (idempotent — safe under theme-editor re-render) ---------- */
  function init() {
    if (document.documentElement.dataset.tiktokShowcaseInit !== 'true') {
      document.documentElement.dataset.tiktokShowcaseInit = 'true';
      document.addEventListener('click', onFacadeClick);
    }
    initVideoAutoplay();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
